import { useQuery } from '@tanstack/react-query'
import { useParams } from '@tanstack/react-router'
import { CheckCircle2, Copy, Loader2 } from 'lucide-react'
import { useState, type ReactNode } from 'react'

import { PixQr } from '../components/pix-qr.tsx'
import { ApiError } from '../lib/api.ts'
import { money } from '../lib/inbox-commerce.ts'
import { fetchPublicPix, publicPixLogoUrl } from '../lib/pix-charge.ts'

/**
 * A página de pagamento do Pix — F207. PÚBLICA: quem abre é o cliente.
 *
 * O mesmo desenho do sistema antigo que o dono mostrou: a logo da loja,
 * "PAGAMENTO VIA PIX", o valor, o nome, o QR, o copia-e-cola e o botão de
 * copiar — e embaixo "Aguardando pagamento…", que vira "Pagamento recebido"
 * quando cai. Pergunta a cada 5 s enquanto está pendente.
 */

const POLL_MS = 5_000

export const PagamentoPage = (): ReactNode => {
  const { token } = useParams({ strict: false })
  const code = typeof token === 'string' ? token : ''
  const [copied, setCopied] = useState(false)
  const [logoFailed, setLogoFailed] = useState(false)

  const payment = useQuery({
    queryKey: ['public-pix', code],
    queryFn: async ({ signal }) => await fetchPublicPix(code, signal),
    enabled: code !== '',
    retry: (count, error) =>
      !(error instanceof ApiError && error.status === 404) && count < 3,
    refetchInterval: (query) =>
      query.state.data?.status === 'pending' ? POLL_MS : false,
  })

  const data = payment.data

  return (
    <main className="flex min-h-dvh items-start justify-center bg-[#f5f7fa] px-4 py-10 text-[#1f2937]">
      <div className="flex w-full max-w-md flex-col items-center gap-4 rounded-2xl border border-[#e5e7eb] bg-white p-6 shadow-lg">
        {payment.isPending ? (
          <Loader2
            aria-label="Carregando"
            className="my-16 h-8 w-8 animate-spin text-[#9ca3af]"
          />
        ) : payment.isError || data === undefined ? (
          <p className="my-12 text-center text-sm text-[#6b7280]">
            {payment.error instanceof ApiError && payment.error.status === 404
              ? 'Link de pagamento não encontrado.'
              : 'Não foi possível carregar o pagamento agora. Tente de novo em instantes.'}
          </p>
        ) : (
          <>
            {data.hasLogo && !logoFailed ? (
              <img
                src={publicPixLogoUrl(code)}
                alt={data.storeName}
                className="max-h-14 max-w-[180px] object-contain"
                onError={() => {
                  setLogoFailed(true)
                }}
              />
            ) : (
              <p className="text-lg font-bold">{data.storeName}</p>
            )}

            <div className="flex flex-col items-center gap-1">
              <p className="text-xs tracking-wide text-[#6b7280] uppercase">
                Pagamento via Pix
              </p>
              <p className="text-3xl font-bold tabular-nums">
                {money(data.amount)}
              </p>
              <p className="text-sm font-semibold">{data.payerName}</p>
            </div>

            {data.status === 'paid' ? (
              <p
                role="status"
                className="my-8 flex items-center gap-2 text-lg font-semibold text-[#15803d]"
              >
                <CheckCircle2 aria-hidden="true" className="h-6 w-6" />
                Pagamento recebido
              </p>
            ) : data.status === 'pending' ? (
              <>
                <PixQr text={data.copyPaste} size={220} />
                <p className="text-center text-xs text-[#6b7280]">
                  Escaneie o QR code no seu app do banco ou copie o código
                  abaixo
                </p>
                <code
                  aria-label="Pix copia e cola"
                  className="w-full rounded-lg border border-[#e5e7eb] bg-[#f9fafb] p-3 text-xs break-all"
                >
                  {data.copyPaste}
                </code>
                <button
                  type="button"
                  className="flex w-full items-center justify-center gap-2 rounded-lg bg-[#eab308] px-4 py-3 font-semibold text-white hover:bg-[#ca8a04]"
                  onClick={() => {
                    void navigator.clipboard
                      .writeText(data.copyPaste)
                      .then(() => {
                        setCopied(true)
                      })
                  }}
                >
                  <Copy aria-hidden="true" className="h-4 w-4" />
                  {copied ? 'Copiado!' : 'Copiar Pix Copia e Cola'}
                </button>
                <p
                  role="status"
                  className="flex items-center gap-2 text-xs text-[#6b7280]"
                >
                  <Loader2
                    aria-hidden="true"
                    className="h-3.5 w-3.5 animate-spin"
                  />
                  Aguardando pagamento…
                </p>
              </>
            ) : (
              <p
                role="status"
                className="my-8 text-center text-sm text-[#6b7280]"
              >
                Este Pix expirou. Peça um novo à loja.
              </p>
            )}
          </>
        )}
      </div>
    </main>
  )
}
